import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { CheckCircle, Cancel, Description } from "@mui/icons-material";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref as storageRef } from "firebase/storage";
import { useFirebase } from "contexts/Firebase";
import {
  CaseWhenBenefit,
  ReimbursementRequest,
  useApproveMonthlyReimbursementMutation,
  useDenyMonthlyReimbursementMutation,
  AdminCaseWhenBenefitsDocument,
} from "services/GraphQl";
import { useNotifications } from "contexts/Notification";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import ApproveReimbursementModal from "./ApproveReimbursementModal";
import moment from "moment";
import DenyReimbursementModal from "./DenyReimbursementModal";

interface PendingReimbursementRequestsTableProps {
  whenBenefit: CaseWhenBenefit;
  companyId: string;
  caseId: string;
}

const PendingReimbursementsTable: React.FC<PendingReimbursementRequestsTableProps> = ({
  whenBenefit,
  caseId,
  companyId,
}) => {
  const reimbursementRequests = whenBenefit.reimbursementRequests || [];
  const whenBenefitType = whenBenefit.distributionType;
  const { storage } = useFirebase();
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ReimbursementRequest | null>(null);
  const [approveMonthlyReimbursement] = useApproveMonthlyReimbursementMutation({
    refetchQueries: [AdminCaseWhenBenefitsDocument]
  });
  const [denyMonthlyReimbursement] = useDenyMonthlyReimbursementMutation({
    refetchQueries: [AdminCaseWhenBenefitsDocument]
  });
  const { notifications } = useNotifications();
  // Filter for pending requests
  const pendingRequests = reimbursementRequests.filter((request) => request.status === "PENDING");

  const formatMonth = (monthStr: string) => {
    return moment(monthStr, "YYYY-MM").format("MMMM YYYY");
  };

  const formatMonths = (coverageMonths: string[]) => {
    if (!coverageMonths || coverageMonths.length === 0) return "—";

    if (coverageMonths.length === 1) {
      return formatMonth(coverageMonths[0]);
    }

    // For multiple months, show range or list
    const sortedMonths = [...coverageMonths].sort();
    if (coverageMonths.length <= 3) {
      return sortedMonths.map(formatMonth).join(", ");
    }

    return `${formatMonth(sortedMonths[0])} - ${formatMonth(sortedMonths[sortedMonths.length - 1])}`;
  };

  const getAvailableAmountForMonths = (coverageMonths: string[]) => {
    if (!coverageMonths || coverageMonths.length === 0) return 0;

    const months = whenBenefit.months || [];
    return coverageMonths.reduce((total, monthStr) => {
      const month = months.find((m) => m.month === monthStr);
      return total + (month?.amount || 0);
    }, 0);
  };

  const handleApproveClick = (request: ReimbursementRequest) => {
    setSelectedRequest(request);
    setApproveModalOpen(true);
  };

  const handleDenyClick = (request: ReimbursementRequest) => {
    setSelectedRequest(request);
    setDenyModalOpen(true);
  };

  const handleApproveConfirm = (requestId: string, note: string) => {
    try {
      approveMonthlyReimbursement({
        variables: {
          requestId: requestId,
          requestUserId: selectedRequest?.userId || "",
          requestBenefitId: caseId || "",
          approvedAmount: selectedRequest?.amount.toString() || "",
          notes: note,
          status: "APPROVED",
          companyId: companyId,
        }
      });
      notifications.success("Reimbursement approved successfully");
    } catch (e) {
      console.error(e);
      notifications.error("Error approving reimbursement");
    }
  };

  const handleDenyConfirm = (requestId: string, note: string) => {
    try {
      denyMonthlyReimbursement({
        variables: {
          requestId: requestId,
          requestUserId: selectedRequest?.userId || "",
          requestBenefitId: caseId || "",
          denialReason: note,
          notes: note,
          companyId: companyId,
        },
      });
      notifications.success("Reimbursement denied successfully");
    } catch (e) {
      console.error(e);
      notifications.error("Error denying reimbursement");
    }
  };

  // V1 approach - component for each file
  const DownloadFile = ({ file }: { file: { name: string; type: string; path: string } }) => {
    const [downloadURL, loading, error] = useDownloadURL(storageRef(storage, file.path));
    
    if (loading) {
      return <Typography variant="body2">{file.name} (Loading...)</Typography>;
    }
    if (error) {
      return (
        <Typography variant="body2" color="error">
          Error: {file.name}: {error.message}
        </Typography>
      );
    }
    if (!downloadURL) return <Typography variant="body2">{file.name}</Typography>;
    
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Description fontSize="small" color="primary" />
        <Button
          variant="text"
          component="a"
          href={downloadURL}
          target="_blank"
          sx={{ 
            textDecoration: "none",
            fontSize: "0.875rem",
            minWidth: "auto",
            padding: 0,
            color: "primary.main",
            textTransform: "none",
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: "transparent"
            }
          }}
        >
          {file.name}
        </Button>
        <Typography variant="caption" color="text.secondary">
          ({file.type})
        </Typography>
      </Box>
    );
  };

  const renderAttachments = (files: any[]) => {
    if (!files || files.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No attachments
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {files.map((file, index) => (
          <DownloadFile key={index} file={file} />
        ))}
      </Box>
    );
  };

  if (pendingRequests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Pending Reimbursement Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No pending reimbursement requests
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
        Pending Reimbursement Requests
      </Typography>

      <TableContainer component={Paper} sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Month(s)</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Amount Available</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Amount Requested</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Attachments</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingRequests.map((request: ReimbursementRequest) => {
              const availableAmount = whenBenefitType === "MONTHLY_ALLOCATION" ? getAvailableAmountForMonths(request.coverageMonths) : whenBenefit.detail?.amountRemaining || 0;

              return (
                <TableRow key={request.reimbursementRequestId} sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}>
                  <TableCell>
                    <Typography variant="body2">{formatMonths(request.coverageMonths)}</Typography>
                  </TableCell>
                  <TableCell>
                    <FormattedCurrency value={availableAmount} />
                  </TableCell>
                  <TableCell>
                    <FormattedCurrency value={request.amount} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>{renderAttachments(request.files)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApproveClick(request)}
                        sx={{
                          color: "success.main",
                          borderColor: "success.main",
                          "&:hover": {
                            backgroundColor: "success.light",
                            borderColor: "success.main",
                          },
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => handleDenyClick(request)}
                        sx={{
                          color: "error.main",
                          borderColor: "error.main",
                          "&:hover": {
                            backgroundColor: "error.light",
                            borderColor: "error.main",
                          },
                        }}
                      >
                        Deny
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ApproveReimbursementModal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        request={selectedRequest}
        onConfirm={handleApproveConfirm}
      />
      <DenyReimbursementModal
        open={denyModalOpen}
        onClose={() => setDenyModalOpen(false)}
        request={selectedRequest}
        onConfirm={handleDenyConfirm}
      />
    </Box>
  );
};

export default PendingReimbursementsTable;
